/**
 * City Building Catalog
 * Shared building types, templates, category assignment, and population logic
 * used by all city district sub-generators.
 */

import type { GridMap, Room, CityDistrictType } from '../types';
import { addEntity, getCell } from '../grid';
import { SeededRandom } from '../random';

// ============================================================================
// BUILDING TYPES
// ============================================================================

export type CityBuildingCategory =
  | 'tavern' | 'inn' | 'temple' | 'blacksmith' | 'apothecary'
  | 'general_store' | 'guard_post' | 'library' | 'stable'
  | 'warehouse' | 'residence';

export interface BuildingTemplate {
  npcRoles: string[];
  furniture: string[];
  frequency: number;
}

export const CATALOG: Record<CityBuildingCategory, BuildingTemplate> = {
  tavern:        { npcRoles: ['Tavernkeeper', 'Barmaid'],  furniture: ['Table', 'Chair', 'Barrel', 'Keg'],   frequency: 2 },
  inn:           { npcRoles: ['Innkeeper'],                furniture: ['Bed', 'Table', 'Chair'],             frequency: 2 },
  blacksmith:    { npcRoles: ['Blacksmith'],               furniture: ['Anvil', 'Forge', 'Crate'],           frequency: 1 },
  temple:        { npcRoles: ['Priest'],                   furniture: ['Altar', 'Bench'],                    frequency: 1 },
  guard_post:    { npcRoles: ['Guard'],                    furniture: ['Table', 'Weapon Rack'],              frequency: 1 },
  general_store: { npcRoles: ['Merchant'],                 furniture: ['Shelf', 'Crate', 'Barrel'],          frequency: 2 },
  library:       { npcRoles: ['Scholar'],                  furniture: ['Bookshelf', 'Desk'],                 frequency: 1 },
  apothecary:    { npcRoles: ['Herbalist'],                furniture: ['Shelf', 'Cauldron'],                 frequency: 1 },
  stable:        { npcRoles: ['Stablehand'],               furniture: ['Hay Bale', 'Trough'],                frequency: 1 },
  warehouse:     { npcRoles: [],                           furniture: ['Crate', 'Barrel', 'Crate'],          frequency: 2 },
  residence:     { npcRoles: [],                           furniture: ['Bed', 'Table', 'Chair'],             frequency: 10 },
};

export const REQUIRED_CATEGORIES: CityBuildingCategory[] = [
  'tavern', 'blacksmith', 'general_store', 'guard_post', 'temple',
];

const ALL_CATEGORIES = Object.keys(CATALOG) as CityBuildingCategory[];

// ============================================================================
// DISTRICT-SPECIFIC CATEGORY BIASES
// ============================================================================

/** Per-district frequency multipliers — higher values make that category more likely. */
const DISTRICT_BIAS: Partial<Record<CityDistrictType, Partial<Record<CityBuildingCategory, number>>>> = {
  market: {
    general_store: 6, tavern: 4, inn: 3, warehouse: 3, apothecary: 2,
    residence: 1,
  },
  noble: {
    library: 4, temple: 3, residence: 8, guard_post: 2,
    tavern: 1, warehouse: 0,
  },
  slums: {
    warehouse: 5, tavern: 4, residence: 8,
    library: 0, temple: 0, guard_post: 1,
  },
  residential: {
    residence: 12, tavern: 2, inn: 2,
  },
  temple: {
    temple: 6, library: 3, residence: 4,
    tavern: 1, warehouse: 0,
  },
  craftsmen: {
    blacksmith: 4, apothecary: 3, warehouse: 3, stable: 2, residence: 6,
  },
  waterfront: {
    warehouse: 5, tavern: 3, inn: 3, stable: 2, residence: 4,
  },
};

// ============================================================================
// CITY BUILDING METADATA
// ============================================================================

export interface CityBuilding {
  room: Room;
  category: CityBuildingCategory;
  doors: Array<{ x: number; y: number }>;
  districtType?: CityDistrictType;
}

// ============================================================================
// CATEGORY ASSIGNMENT
// ============================================================================

export function assignCategories(
  buildings: CityBuilding[],
  cx: number,
  cy: number,
  rng: SeededRandom,
  districtType?: CityDistrictType,
): void {
  if (buildings.length === 0) return;

  // Sort by proximity to center (closest first)
  const byDistance = [...buildings].sort((a, b) => {
    const da = dist(a.room, cx, cy);
    const db = dist(b.room, cx, cy);
    return da - db;
  });

  // Assign required categories to closest buildings (only for first district or no district)
  const assigned = new Set<CityBuilding>();
  if (!districtType) {
    for (const cat of REQUIRED_CATEGORIES) {
      const building = byDistance.find(b => !assigned.has(b));
      if (building) {
        building.category = cat;
        assigned.add(building);
      }
    }
  }

  // Build weighted pool — apply district bias if available
  const biases = districtType ? DISTRICT_BIAS[districtType] : undefined;
  const pool: CityBuildingCategory[] = [];
  for (const cat of ALL_CATEGORIES) {
    const baseFreq = CATALOG[cat].frequency;
    const biasMultiplier = biases?.[cat] ?? baseFreq;
    const count = biases ? biasMultiplier : baseFreq;
    for (let i = 0; i < count; i++) {
      pool.push(cat);
    }
  }

  if (pool.length === 0) {
    // Fallback: just use residence
    pool.push('residence');
  }

  for (const building of buildings) {
    if (!assigned.has(building)) {
      building.category = rng.pick(pool);
    }
  }
}

function dist(room: Room, cx: number, cy: number): number {
  const rx = room.x + room.width / 2;
  const ry = room.y + room.height / 2;
  return Math.abs(rx - cx) + Math.abs(ry - cy);
}

// ============================================================================
// POPULATION (NPCs + Furniture)
// ============================================================================

export function populateBuildings(map: GridMap, buildings: CityBuilding[], rng: SeededRandom): GridMap {
  const occupied = new Set<string>();

  // Mark door tiles and existing entities as occupied
  for (const b of buildings) {
    for (const d of b.doors) occupied.add(`${d.x},${d.y}`);
  }
  for (const e of map.entities) occupied.add(`${e.x},${e.y}`);

  for (const building of buildings) {
    const template = CATALOG[building.category];
    const room = building.room;

    // Place NPCs
    for (let i = 0; i < template.npcRoles.length; i++) {
      const pos = findFreeInteriorTile(map, room, occupied, rng);
      if (pos) {
        occupied.add(`${pos.x},${pos.y}`);
        map = addEntity(map, {
          id: `npc_${room.id}_${i}`,
          x: pos.x, y: pos.y,
          type: 'npc',
          name: template.npcRoles[i],
        });
      }
    }

    // Place furniture (prefer edge tiles)
    const maxFurniture = Math.min(template.furniture.length, room.width * room.height - template.npcRoles.length - 1);
    for (let i = 0; i < maxFurniture; i++) {
      const pos = findFreeEdgeTile(map, room, occupied, rng) ?? findFreeInteriorTile(map, room, occupied, rng);
      if (pos) {
        occupied.add(`${pos.x},${pos.y}`);
        map = addEntity(map, {
          id: `obj_${room.id}_${i}`,
          x: pos.x, y: pos.y,
          type: 'object',
          name: template.furniture[i],
        });
      }
    }

    // Residences have a chance to have a citizen NPC
    if (building.category === 'residence' && rng.bool(0.25)) {
      const pos = findFreeInteriorTile(map, room, occupied, rng);
      if (pos) {
        occupied.add(`${pos.x},${pos.y}`);
        map = addEntity(map, {
          id: `npc_${room.id}_citizen`,
          x: pos.x, y: pos.y,
          type: 'npc',
          name: 'Citizen',
        });
      }
    }
  }

  return map;
}

// ============================================================================
// TILE FINDERS
// ============================================================================

export function findFreeInteriorTile(
  map: GridMap, room: Room, occupied: Set<string>, rng: SeededRandom,
): { x: number; y: number } | null {
  const candidates: Array<{ x: number; y: number }> = [];
  for (let y = room.y; y < room.y + room.height; y++) {
    for (let x = room.x; x < room.x + room.width; x++) {
      const cell = getCell(map, x, y);
      if (cell && cell.terrain === 'floor' && !occupied.has(`${x},${y}`)) {
        candidates.push({ x, y });
      }
    }
  }
  return candidates.length > 0 ? rng.pick(candidates) : null;
}

export function findFreeEdgeTile(
  map: GridMap, room: Room, occupied: Set<string>, rng: SeededRandom,
): { x: number; y: number } | null {
  const candidates: Array<{ x: number; y: number }> = [];
  for (let y = room.y; y < room.y + room.height; y++) {
    for (let x = room.x; x < room.x + room.width; x++) {
      const isEdge = x === room.x || x === room.x + room.width - 1 ||
                     y === room.y || y === room.y + room.height - 1;
      if (!isEdge) continue;
      const cell = getCell(map, x, y);
      if (cell && cell.terrain === 'floor' && !occupied.has(`${x},${y}`)) {
        candidates.push({ x, y });
      }
    }
  }
  return candidates.length > 0 ? rng.pick(candidates) : null;
}
